// components/Process.tsx
"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import BuildIcon from "@mui/icons-material/Build";
import DeploymentIcon from "@mui/icons-material/RocketLaunch";

const processSteps = [
  {
    icon: <SearchIcon sx={{ fontSize: 40 }} />,
    title: "Discovery & Planning",
    description:
      "We start by understanding your business goals, target audience, and technical requirements.",
    activities: [
      "Requirements Analysis",
      "Project Scoping",
      "Timeline Planning",
      "Technical Architecture",
    ],
    color: "#667eea",
  },
  {
    icon: <DesignServicesIcon sx={{ fontSize: 40 }} />,
    title: "Design & Prototyping",
    description:
      "Creating wireframes, mockups, and prototypes to visualize the final product.",
    activities: [
      "UI/UX Design",
      "Wireframing",
      "Prototype Development",
      "Client Feedback",
    ],
    color: "#f093fb",
  },
  {
    icon: <BuildIcon sx={{ fontSize: 40 }} />,
    title: "Development & Testing",
    description:
      "Agile development with continuous testing and regular client demonstrations.",
    activities: [
      "Agile Development",
      "Quality Assurance",
      "Client Demos",
      "Iterative Improvements",
    ],
    color: "#4facfe",
  },
  {
    icon: <DeploymentIcon sx={{ fontSize: 40 }} />,
    title: "Deployment & Support",
    description:
      "Smooth deployment followed by ongoing support and maintenance services.",
    activities: [
      "Production Deployment",
      "Performance Monitoring",
      "Ongoing Support",
      "Regular Updates",
    ],
    color: "#43e97b",
  },
];

export default function Process() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Our Development Process
          </Typography>
          <Typography
            variant="h6"
            component={"p"}
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
            }}
          >
            A transparent, collaborative approach that ensures your
            project&apos;s success from concept to launch
          </Typography>
        </Box>

        {/* Desktop/Tablet View */}
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Grid container spacing={4}>
            {processSteps.map((step, index) => (
              <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    background: "white",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Step Number */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: alpha(step.color, 0.1),
                        color: step.color,
                        fontWeight: 700,
                        fontSize: "1.5rem",
                        mb: 2,
                      }}
                    >
                      {index + 1}
                    </Box>

                    {/* Step Icon */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 80,
                        height: 80,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${step.color} 0%, ${alpha(step.color, 0.7)} 100%)`,
                        color: "white",
                        mb: 3,
                      }}
                    >
                      {step.icon}
                    </Box>

                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {step.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                      sx={{ lineHeight: 1.6 }}
                    >
                      {step.description}
                    </Typography>

                    <Box sx={{ textAlign: "left" }}>
                      {step.activities.map((activity, activityIndex) => (
                        <Typography
                          key={activityIndex}
                          variant="body1"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            color: "text.primary",
                            fontWeight: 500,
                            "&:before": {
                              content: '"▸"',
                              color: step.color,
                              fontWeight: "bold",
                              mr: 1,
                            },
                          }}
                        >
                          {activity}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Mobile View - Stepper */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Stepper orientation="vertical" sx={{ maxWidth: 400, mx: "auto" }}>
            {processSteps.map((step, index) => (
              <Step key={index} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: alpha(step.color, 0.1),
                        color: step.color,
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    {step.title}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      paragraph
                    >
                      {step.description}
                    </Typography>
                    <Box>
                      {step.activities.map((activity, activityIndex) => (
                        <Typography
                          key={activityIndex}
                          variant="body2"
                          fontSize={16}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                            "&:before": {
                              content: '"▸"',
                              color: step.color,
                              fontWeight: "bold",
                              mr: 1,
                            },
                          }}
                        >
                          {activity}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Typography
            variant="h3"
            component={"p"}
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Ready to start your project? Let&apos;s discuss your requirements
            and timeline.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
